import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  BankAccountApiDownload,
  BankAccountDto,
  BankAccountFilterSelects,
  BankAccountFormSelects,
  BankAccountGridDto,
  BankAccountListQuery,
  BankAccountListResult,
  BankAccountRequest,
  BankAccountSelectDto,
  BankAccountToneDto,
  BankAccountingAccountDto,
} from './bank-accounts-api.types';
import { ErpApiError, mapErpError } from './erp-api.errors';
import { ERP_API_URL } from './erp-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const BANK_ACCOUNTS_PATH = '/treasury/bank-accounts';

@Injectable({ providedIn: 'root' })
export class BankAccountsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ERP_API_URL).replace(/\/+$/, '')}${BANK_ACCOUNTS_PATH}`;

  list(query: BankAccountListQuery = {}): Observable<BankAccountListResult> {
    return this.http.get<unknown>(this.baseUrl, { params: listParams(query) }).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(unwrapList),
      catchError(error => throwError(() => mapErpError(error))),
    );
  }

  formSelects(): Promise<BankAccountFormSelects> {
    return this.safeGet(`${this.baseUrl}/form-selects`, unwrapFormSelects);
  }

  filterSelects(): Promise<BankAccountFilterSelects> {
    return this.safeGet(`${this.baseUrl}/filter-selects`, unwrapFilterSelects);
  }

  accountingAccounts(companyUuid: string): Promise<BankAccountingAccountDto[]> {
    return this.safeGet(
      `${this.baseUrl}/form-selects/accounting-accounts`,
      unwrapAccountingAccounts,
      new HttpParams().set('companyUuid', companyUuid),
    );
  }

  getById(uuid: string): Promise<BankAccountDto> {
    return this.safeGet(`${this.baseUrl}/${uuid}`, unwrapAccount);
  }

  create(payload: BankAccountRequest): Promise<BankAccountDto> {
    return this.mutate(this.http.post<unknown>(this.baseUrl, payload), unwrapAccount);
  }

  update(uuid: string, payload: BankAccountRequest): Promise<BankAccountDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapAccount,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(this.http.delete<unknown>(`${this.baseUrl}/${uuid}`), () => undefined);
  }

  exportExcel(query: BankAccountListQuery = {}): Promise<BankAccountApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      listParams(query, false),
      'cuentas-bancarias.xlsx',
    );
  }

  private safeGet<T>(url: string, project: (body: unknown) => T, params?: HttpParams): Promise<T> {
    return this.request(
      this.http
        .get<unknown>(url, { params })
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private mutate<T>(source: Observable<unknown>, project: (body: unknown) => T): Promise<T> {
    return this.request(source, project);
  }

  private async request<T>(source: Observable<unknown>, project: (body: unknown) => T): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapErpError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<BankAccountApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, { params, observe: 'response', responseType: 'blob' })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapErpError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      return timer(seconds * 1000);
    }
    if (error.status >= 500 || error.status === 0) return timer(300);
  }
  return throwError(() => error);
}

function listParams(query: BankAccountListQuery, includePagination = true): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.name) params = params.set('filters[name]', query.name);
  if (query.accountNumber) params = params.set('filters[accountNumber]', query.accountNumber);
  if (query.clabe) params = params.set('filters[clabe]', query.clabe);
  params = setCsv(params, 'select[company]', query.companyUuids);
  params = setCsv(params, 'select[currency]', query.currencyUuids);
  params = setCsv(params, 'select[bank]', query.bankUuids);
  params = setCsv(params, 'select[accountingAccount]', query.accountingAccountUuids);
  params = setCsv(params, 'select[status]', query.statusUuids);
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function setCsv(params: HttpParams, key: string, values: readonly string[] | undefined): HttpParams {
  return values?.length ? params.set(key, values.join(',')) : params;
}

function toDownload(response: HttpResponse<Blob>, fallbackFilename: string): BankAccountApiDownload {
  if (!response.body) throw new ErpApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ?? fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): BankAccountListResult {
  const page = asRecord(unwrapData(body));
  const nested = page?.['data'];
  if (!Array.isArray(nested)) throw new ErpApiError('unexpected', 0);
  const items = nested.map(item => {
    const row = normalizeGrid(item);
    if (!row) throw new ErpApiError('unexpected', 0);
    return row;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return { items, total: typeof totalRecords === 'number' ? totalRecords : items.length };
}

function unwrapAccount(body: unknown): BankAccountDto {
  const account = normalizeDetail(unwrapData(body));
  if (!account) throw new ErpApiError('unexpected', 0);
  return account;
}

function unwrapFormSelects(body: unknown): BankAccountFormSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    banks: selectList(data['banks']),
    currencies: selectList(data['currencies']),
    companies: selectList(data['companies']),
    responsibleEmployees: selectList(data['responsibleEmployees']),
  };
}

function unwrapFilterSelects(body: unknown): BankAccountFilterSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    companies: selectList(data['companies']),
    currencies: selectList(data['currencies']),
    banks: selectList(data['banks']),
    accountingAccounts: selectList(data['accountingAccounts']),
    status: selectList(data['status']),
  };
}

function unwrapAccountingAccounts(body: unknown): BankAccountingAccountDto[] {
  const data = unwrapData(body);
  if (!Array.isArray(data)) throw new ErpApiError('unexpected', 0);
  return data.map(item => {
    const account = accountingAccount(item);
    if (!account) throw new ErpApiError('unexpected', 0);
    return account;
  });
}

function normalizeGrid(value: unknown): BankAccountGridDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const company = stringField(record, 'company');
  const name = blankableString(record, 'name');
  const accountNumber = stringField(record, 'accountNumber');
  const clabe = stringField(record, 'clabe');
  const currency = stringField(record, 'currency');
  const bank = stringField(record, 'bank');
  const accountingAccountName = stringField(record, 'accountingAccount');
  const status = toneDto(record['status']);
  if (
    !uuid ||
    !company ||
    name === undefined ||
    !accountNumber ||
    !clabe ||
    !currency ||
    !bank ||
    !accountingAccountName ||
    !status
  ) {
    return null;
  }
  return {
    uuid,
    company,
    name,
    accountNumber,
    clabe,
    currency,
    bank,
    accountingAccount: accountingAccountName,
    status,
  };
}

function normalizeDetail(value: unknown): BankAccountDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = blankableString(record, 'name');
  const bank = selectDto(record['bank']);
  const accountNumber = stringField(record, 'accountNumber');
  const clabe = stringField(record, 'clabe');
  const currency = selectDto(record['currency']);
  const company = selectDto(record['company']);
  const responsible = optionalSelect(record, 'responsible');
  const account = accountingAccount(record['accountingAccount']);
  const status = toneDto(record['status']);
  const createdAt = stringField(record, 'createdAt');
  const updatedAt = stringField(record, 'updatedAt');
  const notes = nullableString(record, 'notes');
  if (
    !uuid ||
    name === undefined ||
    !bank ||
    !accountNumber ||
    !clabe ||
    !currency ||
    !company ||
    responsible === undefined ||
    !account ||
    !status ||
    !createdAt ||
    !updatedAt ||
    notes === undefined
  ) {
    return null;
  }
  return {
    uuid,
    name,
    bank,
    accountNumber,
    clabe,
    currency,
    company,
    responsible,
    accountingAccount: account,
    notes,
    status,
    createdAt,
    updatedAt,
  };
}

function selectList(value: unknown): BankAccountSelectDto[] {
  if (!Array.isArray(value)) throw new ErpApiError('unexpected', 0);
  return value.map(item => {
    const option = selectDto(item);
    if (!option) throw new ErpApiError('unexpected', 0);
    return option;
  });
}

function accountingAccount(value: unknown): BankAccountingAccountDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  const accountNumber = stringField(record, 'accountNumber');
  const formattedAccountNumber = stringField(record, 'formattedAccountNumber');
  const source = selectDto(record['source']);
  const type = selectDto(record['type']);
  if (
    !uuid ||
    !name ||
    !accountNumber ||
    !formattedAccountNumber ||
    !source ||
    !type ||
    typeof record['affectable'] !== 'boolean' ||
    typeof record['selectable'] !== 'boolean'
  ) {
    return null;
  }
  return {
    uuid,
    name,
    accountNumber,
    formattedAccountNumber,
    source,
    type,
    affectable: record['affectable'],
    selectable: record['selectable'],
  };
}

function nullableString(
  record: Record<string, unknown>,
  key: string,
): string | null | undefined {
  if (!(key in record) || record[key] == null) return null;
  return typeof record[key] === 'string' ? record[key] : undefined;
}

function blankableString(
  record: Record<string, unknown>,
  key: string,
): string | null | undefined {
  if (!(key in record) || record[key] == null) return null;
  const value = record[key];
  if (typeof value !== 'string') return undefined;
  return value.trim() ? value : null;
}

function optionalSelect(
  record: Record<string, unknown>,
  key: string,
): BankAccountSelectDto | null | undefined {
  if (!(key in record) || record[key] == null) return null;
  return selectDto(record[key]) ?? undefined;
}

function selectDto(value: unknown): BankAccountSelectDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  return uuid && name ? { uuid, name } : null;
}

function toneDto(value: unknown): BankAccountToneDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const name = stringField(record, 'name');
  const toneValue = stringField(record, 'value');
  const tone = stringField(record, 'tone');
  return name && toneValue && tone ? { name, value: toneValue, tone } : null;
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}
